import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ChevronLeft, ChevronRight, Plus, Pencil, Search, Trash2 } from "lucide-react";
import { useVehicles } from "@/hooks/useVehicles";
import { deleteVehicle, updateVehicleStatus } from "@/services/vehicles";

const statusPill: Record<string, string> = {
  published: "bg-green-100 text-green-700",
  sold: "bg-red-100 text-red-600",
  draft: "bg-slate-100 text-slate-500",
};
const tabs = ["all", "published", "sold", "draft"] as const;
const statusLabel: Record<string, string> = { published: "Active", draft: "Inactive", sold: "Sold" };
const money = (value: number) => `R ${value.toLocaleString("en-ZA")}`;
const PAGE_SIZE = 10;

const AdminListings: React.FC = () => {
  const { vehicles, loading, error, refresh } = useVehicles();
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState<(typeof tabs)[number]>("all");
  const [page, setPage] = useState(1);
  const [working, setWorking] = useState("");
  const navigate = useNavigate();

  const rows = useMemo(() => vehicles
    .filter((vehicle) => tab === "all" || vehicle.status === tab)
    .filter((vehicle) => `${vehicle.fullTitle} ${vehicle.stockNumber}`.toLowerCase().includes(query.toLowerCase())),
  [vehicles, query, tab]);
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => setPage(1), [query, tab]);
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const setSold = async (id: string) => {
    setWorking(id);
    try {
      await updateVehicleStatus(id, "sold");
      toast.success("Vehicle marked as sold and removed from the public listings.");
      await refresh();
    } catch (cause) {
      toast.error(cause instanceof Error ? cause.message : "Unable to update the vehicle.");
    } finally {
      setWorking("");
    }
  };

  const toggleActive = async (id: string, active: boolean) => {
    setWorking(id);
    try {
      await updateVehicleStatus(id, active ? "published" : "draft");
      toast.success(active ? "Vehicle is active and visible on the website." : "Vehicle is inactive and hidden from the website.");
      await refresh();
    } catch (cause) {
      toast.error(cause instanceof Error ? cause.message : "Unable to update listing visibility.");
    } finally {
      setWorking("");
    }
  };

  const remove = async (id: string, title: string) => {
    if (!window.confirm(`Permanently delete ${title}?`)) return;
    setWorking(id);
    try {
      await deleteVehicle(id);
      toast.success("Vehicle deleted.");
      await refresh();
    } catch (cause) {
      toast.error(cause instanceof Error ? cause.message : "Unable to delete the vehicle.");
    } finally {
      setWorking("");
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div><p className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-400">Inventory</p><h1 className="text-2xl font-extrabold text-slate-900">Vehicle Listings</h1></div>
        <button className="btn-blue py-2.5" data-testid="listings-add" onClick={() => navigate("/admin/listings/new")}><Plus className="h-4 w-4" /> Add New Vehicle</button>
      </div>

      <div className="card-soft p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-1.5">
            {tabs.map((value) => <button key={value} onClick={() => setTab(value)} className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${tab === value ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid={`listings-tab-${value}`}>{value === "all" ? "All" : statusLabel[value]} ({value === "all" ? vehicles.length : vehicles.filter((vehicle) => vehicle.status === value).length})</button>)}
          </div>
          <div className="relative"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search vehicles or stock number..." className="rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm focus:border-blue-400 focus:outline-none" data-testid="listings-search" /></div>
        </div>

        {loading && <p className="py-10 text-center text-slate-400">Loading inventory...</p>}
        {error && <p className="py-10 text-center text-red-600">{error}</p>}
        {!loading && !error && <>
          <div className="mt-4 overflow-x-auto"><table className="w-full min-w-[900px] text-left text-sm">
            <thead><tr className="border-b border-slate-100 text-xs uppercase tracking-wide text-slate-400"><th className="py-3 pr-3">Vehicle</th><th className="py-3 pr-3">Price</th><th className="py-3 pr-3">Price change</th><th className="py-3 pr-3">Status</th><th className="py-3 pr-3">Active</th><th className="py-3 pr-3">Actions</th></tr></thead>
            <tbody>
              {pageRows.map((vehicle) => <tr key={vehicle.databaseId} className="border-b border-slate-50 hover:bg-slate-50/60">
                <td className="py-3 pr-3"><div className="flex items-center gap-3"><img src={vehicle.image} alt={vehicle.fullTitle} className="h-10 w-14 rounded-md object-cover" /><div className="leading-tight"><div className="font-bold text-slate-900">{vehicle.fullTitle}</div><div className="text-xs text-slate-500">Stock {vehicle.stockNumber}</div></div></div></td>
                <td className="py-3 pr-3 font-semibold text-slate-700">{money(vehicle.price)}</td>
                <td className="py-3 pr-3">{vehicle.priceDropAmount > 0 ? <div><span className="rounded-full bg-red-50 px-2 py-1 text-xs font-bold text-red-600">Price dropped</span><div className="mt-1 text-xs text-slate-500">Down {money(vehicle.priceDropAmount)}</div></div> : <span className="text-slate-400">—</span>}</td>
                <td className="py-3 pr-3"><span className={`rounded-full px-2.5 py-1 text-xs font-bold ${statusPill[vehicle.status]}`}>{statusLabel[vehicle.status]}</span></td>
                <td className="py-3 pr-3"><button disabled={working === vehicle.databaseId || vehicle.status === "sold"} onClick={() => toggleActive(vehicle.databaseId, vehicle.status !== "published")} className={`relative h-6 w-11 rounded-full transition-colors disabled:opacity-50 ${vehicle.status === "published" ? "bg-green-500" : "bg-slate-200"}`} aria-label={`${vehicle.status === "published" ? "Deactivate" : "Activate"} ${vehicle.fullTitle}`} aria-pressed={vehicle.status === "published"}><span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${vehicle.status === "published" ? "left-[22px]" : "left-0.5"}`} /></button></td>
                <td className="py-3 pr-3"><div className="flex items-center gap-2"><button onClick={() => navigate(`/admin/listings/${vehicle.databaseId}/edit`)} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"><Pencil className="h-3.5 w-3.5" /> Edit</button>{vehicle.status !== "sold" && <button disabled={working === vehicle.databaseId} onClick={() => setSold(vehicle.databaseId)} className="inline-flex items-center gap-1 rounded-lg border border-green-200 px-2.5 py-1.5 text-xs font-semibold text-green-700 hover:bg-green-50"><CheckCircle2 className="h-3.5 w-3.5" /> Mark sold</button>}<button disabled={working === vehicle.databaseId} onClick={() => remove(vehicle.databaseId, vehicle.fullTitle)} className="text-slate-400 hover:text-red-500" aria-label={`Delete ${vehicle.fullTitle}`}><Trash2 className="h-4 w-4" /></button></div></td>
              </tr>)}
              {!pageRows.length && <tr><td colSpan={6} className="py-10 text-center text-slate-400">No vehicles found.</td></tr>}
            </tbody>
          </table></div>
          {rows.length > 0 && <div className="mt-5 flex flex-col gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-slate-500">Showing <strong className="text-slate-700">{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, rows.length)}</strong> of <strong className="text-slate-700">{rows.length}</strong> vehicles</p>
            <nav className="flex items-center gap-1.5" aria-label="Vehicle listing pages">
              <button type="button" disabled={page === 1} onClick={() => setPage((current) => Math.max(1, current - 1))} className="inline-flex h-9 items-center gap-1 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"><ChevronLeft className="h-4 w-4" /> Previous</button>
              {Array.from({ length: pageCount }, (_, index) => index + 1).map((pageNumber) => <button type="button" key={pageNumber} onClick={() => setPage(pageNumber)} aria-label={`Go to page ${pageNumber}`} aria-current={page === pageNumber ? "page" : undefined} className={`h-9 min-w-9 rounded-lg border px-2 text-xs font-bold ${page === pageNumber ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{pageNumber}</button>)}
              <button type="button" disabled={page === pageCount} onClick={() => setPage((current) => Math.min(pageCount, current + 1))} className="inline-flex h-9 items-center gap-1 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40">Next <ChevronRight className="h-4 w-4" /></button>
            </nav>
          </div>}
        </>}
      </div>
    </div>
  );
};

export default AdminListings;
